import type { BlogLocale } from 'src/data/blog/types';

export interface BlogCopy {
  seoTitle: string;
  seoDescription: string;
  ogLocale: string;
  breadcrumbHome: string;
  breadcrumbBlog: string;
  /** Prima riga del titolo hero (dentro un solo `<h1>`). */
  h1Line1: string;
  /** Seconda riga del titolo hero. */
  h1Line2: string;
  /** Paragrafo introduttivo sotto il titolo. */
  heroSub: string;
  primaryCta: string;
  secondaryCta: string;
  featuredEyebrow: string;
  featuredTitle: string;
  readArticleCta: string;
  articlesTitle: string;
  /** Messaggio quando la lista mostra post nell’altra lingua (hub vuoto nella lingua UI). */
  fallbackGuestLanguageBanner: string;
  emptyState: string;
  productTitle: string;
  productBody: string;
  productCta: string;
  langEn: string;
  langIt: string;
}

/** Headline compatta per JSON-LD Blog / coerenza SEO. */
export function blogHeroHeadlineForSeo(copy: BlogCopy): string {
  return `${copy.h1Line1} ${copy.h1Line2}`.replace(/\s+/g, ' ').trim();
}

const en: BlogCopy = {
  seoTitle: 'Kipify Blog | Ecommerce Analytics, Shopify KPIs & Profit Growth',
  seoDescription:
    'Guides and analysis for Shopify ecommerce teams on store, advertising, GA4 and inventory: ROAS, MER, margins, stock and growth decisions.',
  ogLocale: 'en_US',
  breadcrumbHome: 'Home',
  breadcrumbBlog: 'Blog',
  h1Line1: 'Your ecommerce data is scattered everywhere.',
  h1Line2: 'Your profit should not be.',
  heroSub:
    'Guides and analysis for Shopify ecommerce teams that want to understand what is really happening across their store, advertising, GA4 and inventory: ROAS, MER, margins, stock and growth decisions.',
  primaryCta: 'Read the latest insights',
  secondaryCta: 'See how Kipify works',
  featuredEyebrow: 'Featured',
  featuredTitle: 'Start here',
  readArticleCta: 'Read article',
  articlesTitle: 'Latest',
  fallbackGuestLanguageBanner:
    'The posts below are in Italian only for now; we are working on English versions. Links open the Italian article.',
  emptyState: 'New ecommerce analytics playbooks are coming soon.',
  productTitle:
    'Connect Shopify, ads, GA4 and inventory data in one profit dashboard',
  productBody:
    'Kipify helps ecommerce teams monitor MER, blended ROAS, ACOS, margins, stock cover, product performance and custom KPIs without jumping between fragmented dashboards.',
  productCta: 'Create your Kipify account',
  langEn: 'English',
  langIt: 'Italiano',
};

const it: BlogCopy = {
  seoTitle:
    'Kipify Blog | Analytics e-commerce, KPI Shopify e crescita di profitto',
  seoDescription:
    'Guide e analisi per chi gestisce uno store Shopify: store, advertising, GA4, inventario, ROAS, MER, margini e scorte.',
  ogLocale: 'it_IT',
  breadcrumbHome: 'Home',
  breadcrumbBlog: 'Blog',
  h1Line1: 'I tuoi dati e-commerce sono sparsi ovunque.',
  h1Line2: 'Il profitto no.',
  heroSub:
    'Guide e analisi per chi gestisce un e-commerce Shopify e vuole capire cosa succede davvero tra store, advertising, GA4 e inventario: ROAS, MER, margini, stock e decisioni di crescita.',
  primaryCta: 'Leggi gli ultimi insight',
  secondaryCta: 'Scopri come funziona Kipify',
  featuredEyebrow: 'In evidenza',
  featuredTitle: 'Da qui',
  readArticleCta: "Leggi l'articolo",
  articlesTitle: 'Ultimi',
  fallbackGuestLanguageBanner:
    'Articoli disponibili solo in inglese: stiamo preparando le versioni italiane. I link aprono il post in inglese.',
  emptyState: 'Arrivano presto nuovi playbook di analytics e-commerce.',
  productTitle:
    'Collega Shopify, ads, GA4 e inventario in un’unica dashboard di profitto',
  productBody:
    'Kipify aiuta i team e-commerce a monitorare MER, blended ROAS, ACOS, margini, stock cover, performance prodotto e KPI personalizzati senza saltare tra dashboard frammentate.',
  productCta: 'Crea il tuo account Kipify',
  langEn: 'English',
  langIt: 'Italiano',
};

export function getBlogCopy(locale: BlogLocale): BlogCopy {
  return locale === 'it' ? it : en;
}
